/* ==========================================================================
   Executive Beverage — Estimate builder
   --------------------------------------------------------------------------
   A stepped, stateful quote flow with a live running total. Modeled on the
   service-booking pattern (Safelite and similar): qualify first, quote as you
   go, ask for contact details last — once the visitor has already seen value.

   Deliberate constraints:
   - No dependencies. Vanilla DOM.
   - PROGRESSIVE ENHANCEMENT. The markup ships as one plain form with every
     field visible and a real submit button. This script only *then* splits it
     into steps. If it fails to load, the form still works — it just looks
     long. The revenue path must never depend on JavaScript.
   - Submits through Shopify's native contact form, so there is no app and no
     third-party host in the lead path.
   - The running total is an ESTIMATE, never a checkout. Nothing here takes
     payment or writes an order.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-eb-estimate]');
  if (!root) return;

  /* ---- Mode chooser -----------------------------------------------------
     Build-your-own versus have-a-pro-call-me. Runs before the stepped-form
     setup so it still works on a page where the builder is absent (for
     instance after a successful post, when only the confirmation renders). */
  (function modes() {
    var bar = root.querySelector('[data-eb-modes]');
    if (!bar) return;

    // NOT [data-eb-mode] — that attribute is already used by catalog item rows
    // for their billing mode, so this selector was matching 32 product rows.
    var buttons = [].slice.call(bar.querySelectorAll('[data-eb-mode-btn]'));
    var panels = [].slice.call(root.querySelectorAll('[data-eb-mode-panel]'));

    function activate(mode) {
      buttons.forEach(function (b) {
        var on = b.getAttribute('data-eb-mode-btn') === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      panels.forEach(function (p) {
        p.hidden = p.getAttribute('data-eb-mode-panel') !== mode;
      });
    }

    /* Deep-link support: /pages/book-your-event#callback opens the short form.
       This is ALSO how the right confirmation gets shown: Shopify's success
       redirect anchors to the id of the form that posted, and the quick form's
       id is eb-callback — so a quick submission returns with #eb-callback and
       lands back on the quick panel, now showing its confirmation. Both forms
       set posted_successfully together (it is page-level), so panel exclusivity
       is what keeps the other form's confirmation out of sight. */
    var wantsQuick = (window.location.hash || '').toLowerCase().indexOf('callback') > -1;

    // On a success page there is nothing to switch between — the confirmation
    // is the whole content, so the chooser would just be noise. Which PANEL
    // shows still matters though, so the hash check runs before the bail-out.
    if (root.querySelector('.eb-est__done')) {
      bar.hidden = true;
      if (wantsQuick) activate('quick');
      return;
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        activate(b.getAttribute('data-eb-mode-btn'));
      });
    });

    if (wantsQuick) activate('quick');
  })();

  var form = root.querySelector('form');
  var steps = [].slice.call(root.querySelectorAll('[data-eb-step]'));
  if (!form || steps.length < 2) return;

  var progressBar = root.querySelector('[data-eb-progress-bar]');
  var progressText = root.querySelector('[data-eb-progress-text]');
  var summaryList = root.querySelector('[data-eb-summary-list]');
  var totalEl = root.querySelector('[data-eb-total]');

  /* ---- Mobile running total -------------------------------------------
     Mirrors the rail's figure into a fixed bottom bar on phones, where the
     rail itself sits below every question. Deliberately a MIRROR and not a
     second calculation: an observer copying one node's text can never drift
     from it, whereas a second call into the maths eventually will.

     The bar reveals itself the moment there is a real number, so it never
     covers content while it would only say "—". */
  var mobileBar = root.querySelector('[data-eb-mobilebar]');
  var mobileVal = root.querySelector('[data-eb-mobilebar-value]');
  var mobileJump = root.querySelector('[data-eb-mobilebar-jump]');

  function syncMobileBar() {
    if (!mobileBar || !mobileVal || !totalEl) return;
    var text = (totalEl.textContent || '').trim();
    mobileVal.textContent = text;
    var hasFigure = text && text !== '\u2014' && text !== '-';
    if (hasFigure) mobileBar.removeAttribute('hidden');
    else mobileBar.setAttribute('hidden', '');
  }

  if (totalEl && mobileBar && typeof MutationObserver !== 'undefined') {
    new MutationObserver(syncMobileBar).observe(totalEl, {
      childList: true, characterData: true, subtree: true
    });
    syncMobileBar();
  }

  /* iOS shrinks the VISUAL viewport when the keyboard opens but position: fixed
     still measures against the LAYOUT viewport, so a bottom-pinned bar drifts up
     over the content and jitters while you type. That is the glitch on the guest
     counter — the field itself is fine.

     Hiding the bar while any field is focused removes it entirely. The running
     total is not useful mid-keystroke anyway, and it reappears the moment the
     keyboard closes. Where visualViewport exists we also follow the real
     viewport, which covers Android and iPad split-keyboard cases. */
  var barSuppressed = false;
  function suppressBar(on) {
    barSuppressed = on;
    if (!mobileBar) return;
    if (on) mobileBar.setAttribute('hidden', '');
    else syncMobileBar();
  }
  root.addEventListener('focusin', function (e) {
    if (e.target.matches('input, select, textarea')) suppressBar(true);
  });
  root.addEventListener('focusout', function () {
    // let the keyboard finish closing before measuring again
    setTimeout(function () {
      var a = document.activeElement;
      if (!a || !a.matches || !a.matches('input, select, textarea')) suppressBar(false);
    }, 250);
  });
  if (window.visualViewport && mobileBar) {
    window.visualViewport.addEventListener('resize', function () {
      var shrunk = window.visualViewport.height < window.innerHeight - 120;
      if (shrunk) mobileBar.setAttribute('hidden', '');
      else if (!barSuppressed) syncMobileBar();
    });
  }

  if (mobileJump) {
    mobileJump.addEventListener('click', function () {
      var summary = root.querySelector('.eb-est__summary');
      if (summary) summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  var totalNote = root.querySelector('[data-eb-total-note]');
  var specField = root.querySelector('[data-eb-spec]');

  var current = 0;
  var reached = 0;
  var progressSteps = [].slice.call(root.querySelectorAll('[data-eb-goto]'));

  /* ---- Arm stepping (this is what JS adds) ----------------------------- */
  root.setAttribute('data-eb-stepped', '');

  /* The services band. Staffing and rentals are firm; consumables move with the
     final headcount, so the top of the range carries the contingency. Fees are
     recomputed on the band rather than stretched across it. */
  var BAND = 1.15;

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function fieldsIn(step) {
    return [].slice.call(step.querySelectorAll('input, select, textarea'));
  }

  /* ---- Validation ------------------------------------------------------
     Per-step, using the browser's own constraint validation so we inherit
     required/type/min semantics rather than reimplementing them. */
  function validateStep(index) {
    var ok = true;
    var firstBad = null;
    fieldsIn(steps[index]).forEach(function (el) {
      if (el.disabled || el.type === 'hidden') return;
      var valid = el.checkValidity();
      // Radio groups: valid if any in the group is checked.
      if (el.type === 'radio' && el.name) {
        valid = !!root.querySelector('input[name="' + el.name + '"]:checked') || !el.required;
      }
      var wrap = el.closest('[data-eb-field]') || el.parentElement;
      if (!valid) {
        ok = false;
        if (wrap) wrap.setAttribute('data-eb-invalid', '');
        if (!firstBad) firstBad = el;
      } else if (wrap) {
        wrap.removeAttribute('data-eb-invalid');
      }
    });
    if (firstBad) firstBad.focus();
    return ok;
  }

  /* ---- Step navigation ------------------------------------------------- */

  /* False until the first render has happened. show() scrolls the active step
     under the header, which is right when you press Continue and wrong on page
     load: arriving from a "Build your estimate" link, it scrolled you straight
     past the section heading, the explanation of how the estimate works and the
     two buttons — including "Talk to a pro", which is the whole alternative for
     someone who does not want to fill in a form. A restored draft is the same
     case: land at the top, let the progress rail show where you left off. */
  var booted = false;

  /* ---- Browser history ------------------------------------------------------
     Every step the visitor moves to gets its own history entry, so the browser's
     Back and Forward buttons, the phone's back gesture and the keyboard shortcut
     all move between STEPS. The wizard used to leave history alone, so pressing
     Back on step 3 left the page entirely: the visitor lost their place and had
     to trust the saved-draft prompt to get it back.

     Two decisions worth keeping:
       - The step is stored in history.state itself, not in a list held in
         memory. After a reload the earlier entries are still in the browser's
         history carrying their own step numbers, and a Back press has to keep
         working; an in-memory list would be gone and Back would do nothing.
       - The URL is never changed. A new hash or query would register as a new
         page in analytics and the ad pixels, which a step is not. */
  var hasHistory = !!(window.history && history.pushState && history.replaceState);

  function stampHistory(before, fromHistory) {
    if (!hasHistory || fromHistory) return;
    var base = history.state && typeof history.state === 'object' ? history.state : {};
    var state = {};
    for (var key in base) { if (Object.prototype.hasOwnProperty.call(base, key)) state[key] = base[key]; }
    state.ebWizard = true;
    state.step = current;
    state.prev = before;   // step shown by the entry directly behind this one
    try {
      if (!booted) history.replaceState(state, '');           // first render: label, do not add
      else if (before !== current) history.pushState(state, '');
    } catch (e) { /* history locked down (some in-app browsers): steps still work */ }
  }

  function show(index, fromHistory) {
    var before = current;
    current = Math.max(0, Math.min(index, steps.length - 1));
    steps.forEach(function (s, i) {
      var on = i === current;
      s.hidden = !on;
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    var pct = ((current + 1) / steps.length) * 100;
    if (progressBar) progressBar.style.width = pct + '%';
    if (progressText) {
      progressText.textContent = 'Step ' + (current + 1) + ' of ' + steps.length;
    }

    /* Furthest step reached, so a step you have already completed stays
       reachable after you jump backwards. */
    if (current > reached) reached = current;
    progressSteps.forEach(function (btn, i) {
      btn.disabled = i > reached;
      btn.classList.toggle('is-done', i < current);
      if (i === current) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');
    });
    /* Put the top of the STEP in view — not the top of the section.
       This used to measure `root`, which is the whole estimate section including
       its heading, intro copy and progress rail, so every Continue threw you back
       up past all of it and read as "it jumped to the top of the page". The step
       panel is what changed, so the step panel is what should land under the
       header.

       Only scrolls when the target is actually out of view, in either direction:
       the old check was one-way, so advancing to a short step could leave you
       reading its middle. And never while a field is focused — a smooth scroll
       competing with the iOS keyboard animation is the other half of the jitter. */
    var typing = document.activeElement &&
                 document.activeElement.matches &&
                 document.activeElement.matches('input, select, textarea');
    if (!typing && booted) {
      var panel = steps[current] || root;
      var top = panel.getBoundingClientRect().top + window.pageYOffset - 90;
      if (Math.abs(window.pageYOffset - top) > 24) {
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }
    stampHistory(before, fromHistory);
  }

  /* Back and Forward land here. Only entries this wizard stamped are ours; a
     popstate from a plain #hash link carries no state and is left alone.
     Moving forward through history does not re-validate: those steps were
     already completed, and the submit still validates everything. */
  window.addEventListener('popstate', function (event) {
    var st = event.state;
    if (!st || !st.ebWizard || typeof st.step !== 'number') return;
    if (st.step === current) return;
    show(st.step, true);
  });

  root.addEventListener('click', function (event) {
    var goto = event.target.closest('[data-eb-goto]');
    if (goto && !goto.disabled) {
      event.preventDefault();
      show(parseInt(goto.getAttribute('data-eb-goto'), 10) || 0);
      return;
    }
    var next = event.target.closest('[data-eb-next]');
    var prev = event.target.closest('[data-eb-prev]');
    if (next) {
      event.preventDefault();
      if (validateStep(current)) show(current + 1);
      return;
    }
    if (prev) {
      event.preventDefault();
      /* In the normal straight-through case the entry behind this one IS the
         previous step, so go back through history. That keeps the stack the
         same length as the steps instead of growing a new entry on every Back,
         and the browser's own Back then continues from the right place.

         When the visitor jumped here (the progress markers) the entry behind is
         some other step, so history.back() would take them somewhere they did
         not ask for. Step back explicitly instead. */
      var st = hasHistory ? history.state : null;
      if (st && st.ebWizard && st.step === current && st.prev === current - 1) history.back();
      else show(current - 1);
    }
  });

  // Choice cards double as "pick and advance".
  root.addEventListener('change', function (event) {
    var el = event.target;
    if (el.matches('[data-eb-advance]') && el.checked) {
      if (validateStep(current)) window.setTimeout(function () { show(current + 1); }, 160);
    }
    recalc();
  });
  root.addEventListener('input', function (event) {
    if (event.target.matches('[data-eb-qty]')) {
      event.target.setAttribute('data-eb-touched', '');
    }
    recalc();
  });

  /* A field sitting at 0 is a starting point, not an answer. Clicking into one
     to type "2" meant selecting or backspacing the 0 first, and on a phone that
     is a fiddly thing to ask before someone can answer "how many bartenders".

     So: focus a numeric field showing exactly 0 and it empties, ready to type.
     Leave without typing and the 0 comes back, so the field never reads as
     blank-and-unanswered when the estimate is counting it as none.

     Deliberately NOT once-only. A one-shot flag would clear the first time and
     then trap the visitor with a stubborn 0 on every visit after, which is the
     annoyance this removes. Whenever it shows 0, it clears.

     Two things it must not do:
       - fire `input`, which would set data-eb-touched and permanently stop the
         recommended quantity from seeding a row nobody has typed in
       - fire recalc, which would flash the running total to 0 mid-edit
     Assigning .value does neither, so both are had for free. */
  /* ---- Staffing only -------------------------------------------------
     Turns the longest step into the short one for anyone who has their own
     bar: hides every goods section and empties whatever was ticked in them,
     so the estimate cannot quietly price a bar nobody can see.

     It CLEARS rather than disables, and that is the whole trick. Disabling
     would collide head-on with the event-type logic, which owns the disabled
     state of the wedding-only crush kit block and re-asserts it on every
     change — switching this off would have re-enabled a wedding block for a
     corporate booking. Unticked boxes and empty fields are not submitted
     anyway, so clearing achieves the same result and leaves that ownership
     alone.

     Hiding is done in CSS off a class on the root, not by setting `hidden`
     here, for the same reason: `hidden` is the event-type logic's to set. */
  (function staffOnly() {
    var btn = root.querySelector('[data-eb-staff-only]');
    if (!btn) return;
    var field = root.querySelector('[data-eb-staff-only-field]');

    function clearGoods() {
      [].slice.call(root.querySelectorAll('[data-eb-goods]')).forEach(function (box) {
        [].slice.call(box.querySelectorAll('input, select, textarea')).forEach(function (el) {
          if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) {
              el.checked = false;
              /* Through the real event, so the row's own listeners run: the
                 quantity input hides itself and releases its typed figure back
                 to the suggestion. */
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (el.tagName === 'SELECT') {
            if (el.selectedIndex > 0) {
              el.selectedIndex = 0;
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (String(el.value).trim() !== '') {
            el.value = '';
            el.removeAttribute('data-eb-touched');
          }
        });
      });
    }

    btn.addEventListener('click', function () {
      var on = btn.getAttribute('aria-pressed') !== 'true';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      root.classList.toggle('eb-est--staff-only', on);
      if (field) field.value = on ? 'Yes, staffing only' : '';
      if (on) clearGoods();
      recalc();
    });
  })();

  function zeroClearable(el) {
    return el
      && el.tagName === 'INPUT'
      && el.getAttribute('inputmode') === 'numeric'
      && !el.disabled
      && !el.readOnly;
  }

  root.addEventListener('focusin', function (event) {
    var el = event.target;
    if (!zeroClearable(el)) return;
    if (String(el.value).trim() !== '0') return;
    el.value = '';
    el.setAttribute('data-eb-zero-cleared', '');
  });

  root.addEventListener('focusout', function (event) {
    var el = event.target;
    if (!zeroClearable(el)) return;
    if (!el.hasAttribute('data-eb-zero-cleared')) return;
    el.removeAttribute('data-eb-zero-cleared');
    if (String(el.value).trim() === '') el.value = '0';
  });

  // Unticking an item releases its quantity back to the suggested figure.
  root.addEventListener('change', function (event) {
    var row = event.target.closest('[data-eb-item]');
    if (!row || event.target.type !== 'checkbox') return;
    if (!event.target.checked) {
      var q = row.querySelector('[data-eb-qty]');
      if (q) q.removeAttribute('data-eb-touched');
      var w = row.querySelector('[data-eb-qty-wrap]');
      if (w) w.hidden = true;
    }
  });

  // Enter should advance a step, not submit the whole form early.
  form.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (event.target.tagName === 'TEXTAREA') return;
    if (current < steps.length - 1) {
      event.preventDefault();
      if (validateStep(current)) show(current + 1);
    }
  });

  // Final guard: never let the form submit from a non-final step.
  form.addEventListener('submit', function (event) {
    if (current < steps.length - 1) {
      event.preventDefault();
      if (validateStep(current)) show(current + 1);
      return;
    }
    // Last line of defence. Whatever happened during the session, nothing
    // belonging to an unticked row goes in the email.
    syncQtySubmission();
    if (!validateStep(current)) {
      event.preventDefault();
      return;
    }
    stripEmptyFields();
    formatTimeFieldsForEmail();
    armStallNotice();
  });

  /* ---- Live estimate ---------------------------------------------------
     Reads data-eb-rate / data-eb-qty-for off the inputs, so pricing lives in
     the section schema and this file needs no edit when rates change. */
  /* Metadata lives on the WRAPPING element, not the input. A legacy theme
     script rewrites input[type=number] into its own quantity markup and strips
     data-* attributes in the process; wrappers survive that. numOf() therefore
     resolves a value from a wrapper by looking inside it. */
  function numOf(scope) {
    if (!scope) return 0;
    var el = scope.matches('input, select') ? scope : scope.querySelector('input, select');
    return el ? parseFloat(String(el.value).replace(/[^0-9.]/g, '')) || 0 : 0;
  }

  /* An unticked row must not submit a quantity.

     This was a live data bug, not a cosmetic one. Ticking an item seeds its
     quantity box from the headcount; unticking it hid the box and cleared the
     "touched" marker but left the VALUE in place on an enabled input. The
     on-screen summary filters on `checked` so it looked correct, while the form
     still posted `Qty — Cactus Coupe Glass: 120` — the office received items the
     customer had explicitly deselected.

     Disabled rather than merely emptied: a disabled control is never submitted,
     so even if some other code path repopulates the value it cannot leak. And it
     runs from recalc() on every change instead of only in the uncheck handler,
     so the state cannot drift out of step however the row got toggled. */
  function syncQtySubmission() {
    var rows = root.querySelectorAll('[data-eb-item]');
    for (var i = 0; i < rows.length; i++) {
      var box = rows[i].querySelector('input[type="checkbox"]');
      var qty = rows[i].querySelector('[data-eb-qty]');
      if (!qty) continue;
      var on = !!(box && box.checked);
      qty.disabled = !on;
      if (!on) {
        qty.value = '';
        qty.removeAttribute('data-eb-touched');
      }
    }
  }

  /* An empty field is not information, and this form posts a lot of them: a
     measured submission sent 52 fields of which 35 were blank — Vessel, Marina,
     Push-off time, all belonging to event types the customer never picked. Two
     thirds of every estimate email was therefore whitespace to scroll past
     before reaching anything real.

     Disabled fields are not submitted, so blanking them at the moment of
     submit removes them from the email without touching what the visitor sees
     or having to maintain a list of which fields belong to which event type.

     Two deliberate exceptions:
       - required fields are left alone, because disabling one would skip the
         browser's own validation and let the form submit without it
       - checkboxes and radios are skipped, since an unchecked one never posts
         in the first place */
  /* Never strip these, however empty they look. They are the form's plumbing,
     not answers to questions.

     This caused a real outage. Shopify protects /contact with invisible
     hCaptcha, and it binds LAZILY — on the first focusin or change inside the
     form, not on page load. Once bound, it injects its token field into the
     form, and that field is EMPTY until the challenge resolves. This function
     then disabled it along with every other blank field, and a disabled field
     is never posted, so the estimate arrived at Shopify with no token and came
     back "Missing CAPTCHA token". Only this form broke, because only this form
     strips fields.

     Names taken from Shopify's own captcha bootstrap, plus a catch-all, so a
     rename on their side cannot quietly reintroduce it. */
  var KEEP_ALWAYS = /captcha|^form_type$|^utf8$|^form_key$|^tags$|^password$|^recaptcha-v3-token$/i;

  function stripEmptyFields() {
    var fields = form.querySelectorAll('input, select, textarea');
    var removed = 0;
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      if (el.disabled || !el.name || el.required) continue;
      if (KEEP_ALWAYS.test(el.name)) continue;
      if (el.type === 'checkbox' || el.type === 'radio') continue;
      if (String(el.value == null ? '' : el.value).trim() !== '') continue;
      el.disabled = true;
      el.setAttribute('data-eb-stripped', '');
      removed++;
    }
    return removed;
  }

  /* Undo the strip if the submit did not actually leave the page.

     Disabling a field at submit is only safe if something puts it back. A
     blocked submit — failed server validation, the AJAX handler refusing, a
     network drop — would otherwise leave the visitor with a form whose empty
     fields are permanently dead and untypeable. Restoring on the next
     interaction costs nothing and removes that whole failure mode. */
  function restoreStrippedFields() {
    var held = form.querySelectorAll('[data-eb-stripped]');
    for (var i = 0; i < held.length; i++) {
      held[i].disabled = false;
      held[i].removeAttribute('data-eb-stripped');
    }
  }

  /* type="time" always stores and would submit its value as 24-hour "HH:MM" —
     that is the HTML5 spec, not a browser locale setting, so there is no input
     attribute that changes it. Shopify's contact form email just prints
     whatever value each field submits with, so left alone every time field
     (ceremony start, bar hours, boat push-off, etc.) lands in Dennis's inbox
     as "14:30" instead of "2:30 PM".

     A time input rejects any value that is not HH:MM — setting .value to a
     12-hour string is silently ignored — so the field is switched to type
     "text" right at submit, after formatting, the same moment
     stripEmptyFields() disables the empty ones. */
  function formatTimeForEmail(value) {
    var parts = value.split(':');
    var hour = parseInt(parts[0], 10);
    var minute = parts[1];
    var suffix = hour >= 12 ? 'PM' : 'AM';
    var hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    return hour12 + ':' + minute + ' ' + suffix + ' ET';
  }

  function formatTimeFieldsForEmail() {
    var fields = form.querySelectorAll('input[type="time"]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      if (!el.value) continue;
      var raw = el.value;
      var pretty;
      try { pretty = formatTimeForEmail(raw); } catch (e) { continue; }
      el.setAttribute('data-eb-raw-time', raw);
      /* Type FIRST, then value. A time input sanitises anything that is not
         HH:MM to an empty string, so assigning "2:30 PM" while it was still
         type="time" threw the value away and every time field was submitted
         blank. The order is the whole fix. */
      el.type = 'text';
      el.value = pretty;
    }
  }

  /* Undo alongside restoreStrippedFields(), for the same reason: a blocked
     submit must not leave a time field stuck as plain text a visitor can no
     longer pick from a clock UI. */
  function restoreTimeFields() {
    var held = form.querySelectorAll('[data-eb-raw-time]');
    for (var i = 0; i < held.length; i++) {
      held[i].type = 'time';
      held[i].value = held[i].getAttribute('data-eb-raw-time');
      held[i].removeAttribute('data-eb-raw-time');
    }
  }

  /* A submit that never leaves the page loses the booking and tells the
     visitor nothing. Shopify's captcha takes over the submit on first
     interaction: it cancels the normal post, runs hCaptcha, and posts from its
     own callback. If hCaptcha cannot run (ad blockers, privacy browsers,
     in-app browsers, a dropped connection) it only writes "form submit failed"
     to the console and the button looks dead.

     So: a valid final submit, and fifteen seconds later this page is still
     here, means it did not go. Say so plainly and offer a way through that
     does not depend on the captcha. Wording is deliberately calm because the
     same fifteen seconds also covers someone slowly solving a visible hCaptcha
     puzzle, and it must not read as an accusation of failure while they do. */
  var stallTimer = null;
  function armStallNotice() {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(showStallNotice, 15000);
  }
  function showStallNotice() {
    restoreStrippedFields();
    restoreTimeFields();
    var note = form.querySelector('[data-eb-stalled]');
    if (!note) {
      note = document.createElement('div');
      note.className = 'eb-est__errors';
      note.setAttribute('role', 'alert');
      note.setAttribute('data-eb-stalled', '');
      note.innerHTML = '<strong>Still sending&hellip;</strong> If nothing has happened, ' +
        'your request has not gone through yet. Press the button once more, or call ' +
        '<a href="tel:+17579955855">757-995-5855</a> or email ' +
        '<a href="mailto:events@execbeverage.com">events@execbeverage.com</a> ' +
        'and we will take it from there.';
      var btn = form.querySelector('button[type="submit"]');
      var anchor = btn ? (btn.closest('.eb-est__nav') || btn.parentNode) : null;
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor);
      else form.appendChild(note);
    }
    note.hidden = false;
  }

  /* How many ticked items share a guest's drinking. Four glass types on one
     menu do not each need a glass per guest — the same guest uses one of them
     at a time — so the per-guest allowance is divided between them.

     Counted per GROUP, because glassware competes with glassware and ice does
     not compete with either. */
  function sharedCount(row) {
    /* Ice never shares. Cubed, crushed and whiskey cubes are not alternatives
       to one another — a party pouring frozen drinks AND rocks drinks needs
       both, in full. */
    if (row.closest('[data-eb-ice-group]')) return 1;
    var group = row.closest('.eb-est__opts');
    if (!group) return 1;
    var rows = group.querySelectorAll('[data-eb-item]');
    var n = 0;
    for (var i = 0; i < rows.length; i++) {
      var box = rows[i].querySelector('input[type="checkbox"]');
      if (box && box.checked) n++;
    }
    return n > 0 ? n : 1;
  }

  /* Pack items are sold whole — 25 cubes, 25 glasses — so a suggestion of
     "30" is not a real quantity anyone can order; it is 30 individual pieces
     that Math.ceil(qty / pack) at checkout would round up to 50 anyway,
     silently doubling the visible number. Round the suggestion itself to the
     nearest pack, floored at one full pack, so what a host sees is always
     something they can actually buy. */
  function roundToPack(n, pack) {
    if (!pack || pack <= 1) return n;
    var rounded = Math.round(n / pack) * pack;
    return rounded < pack ? pack : rounded;
  }

  /* The low/high band rounds OUTWARD (floor/ceiling), not to the nearest
     pack. Rounding both ends to the nearest pack collapses a tight raw
     range — 10 guests is 8-12 cubes — onto the same multiple and then a
     patched-open range overstates it as "25-50" for a party that only ever
     needs one bag. Floor/ceiling keeps the band honest: it only widens past
     one pack when the raw spread actually crosses a pack boundary. */
  function floorToPack(n, pack) {
    if (!pack || pack <= 1) return n;
    var floored = Math.floor(n / pack) * pack;
    return floored < pack ? pack : floored;
  }
  function ceilToPack(n, pack) {
    if (!pack || pack <= 1) return n;
    var ceiled = Math.ceil(n / pack) * pack;
    return ceiled < pack ? pack : ceiled;
  }

  function recalc() {
    restoreStrippedFields();
    restoreTimeFields();
    syncQtySubmission();

    var lines = [];
    var subtotal = 0;

    var serviceHours = numOf(root.querySelector('[data-eb-hours]'));
    var totalGuests = numOf(root.querySelector('[data-eb-guests]'));
    var kids = numOf(root.querySelector('[data-eb-kids]'));
    // Never let an over-stated kid count produce a negative adult count.
    if (kids > totalGuests) kids = totalGuests;
    var ofAge = Math.max(0, totalGuests - kids);

    /* The stated drinker count wins when given. Over-21 is not the same as
       drinking, and the alcohol side of the estimate should follow the real
       figure — falling back to all adults only when nobody has said. */
    var drinkersEl = root.querySelector('[data-eb-drinkers]');
    var stated = numOf(drinkersEl);
    var statedRaw = drinkersEl ? (drinkersEl.querySelector('input') || {}).value : '';
    var adults = (statedRaw && String(statedRaw).trim() !== '') ? Math.min(stated, totalGuests) : ofAge;

    var adultsNote = root.querySelector('[data-eb-adults-note]');
    if (adultsNote) {
      adultsNote.textContent = totalGuests > 0
        ? 'Drinkers: ' + adults.toLocaleString('en-US') +
          (statedRaw && String(statedRaw).trim() !== '' ? '' : ' (all ' + ofAge + ' adults)')
        : 'Drinkers: —';
    }

    /* ---- Setup & breakdown ------------------------------------------------
       Crew are on site before and after service, so those hours are staffed
       hours. Count bars first: the windows widen with bar count and event size.
       Every figure and threshold is a section setting — the defaults encode
       Dennis's stated rules but the "large event" cut-off is a placeholder. */
    var barCount = [].slice.call(root.querySelectorAll('[data-eb-item][data-eb-mode="flat"]'))
      .filter(function (r) { var i = r.querySelector('input'); return i && i.checked; }).length;

    var attr = function (name, fallback) {
      var v = parseFloat(root.getAttribute(name));
      return isFinite(v) ? v : fallback;
    };
    var largeAt = attr('data-eb-large-threshold', 500);
    var isLarge = totalGuests >= largeAt;

    var setupHrs, cleanupHrs, sizeNote;
    if (isLarge) {
      setupHrs = attr('data-eb-setup-large', 6);
      cleanupHrs = attr('data-eb-cleanup-large', 2);
      sizeNote = 'large event';
    } else if (barCount >= 2) {
      setupHrs = attr('data-eb-setup-multi-bar', 3);
      cleanupHrs = attr('data-eb-cleanup-multi-bar', 1.5);
      sizeNote = barCount + ' bars';
    } else if (barCount === 1) {
      setupHrs = attr('data-eb-setup-one-bar', 1.5);
      cleanupHrs = attr('data-eb-cleanup-base', 1);
      sizeNote = 'bar delivery & setup';
    } else {
      setupHrs = attr('data-eb-setup-no-bar', 1);
      cleanupHrs = attr('data-eb-cleanup-base', 1);
      sizeNote = 'no bar';
    }

    var billSetup = root.getAttribute('data-eb-bill-setup') === 'true';
    var extraHrs = billSetup ? setupHrs + cleanupHrs : 0;
    var hours = serviceHours + extraHrs;

    if (serviceHours > 0) {
      lines.push({
        label: 'Setup & breakdown',
        detail: setupHrs + ' hrs before + ' + cleanupHrs + ' hrs after (' + sizeNote + ')',
        amount: null,
        info: billSetup ? 'in staffing' : 'not billed'
      });
    }

    // Hourly staffing: quantity x staffed hours x rate

    [].slice.call(root.querySelectorAll('[data-eb-rate-hourly]')).forEach(function (el) {
      var qty = numOf(el);
      if (qty <= 0) return;
      var rate = parseFloat(el.getAttribute('data-eb-rate-hourly')) || 0;
      var label = el.getAttribute('data-eb-label') || 'Staff';
      var rateNote = '';

      /* Rate overrides. The event type governs EVERY staffed role — on a boat the
         whole crew is $50/hr, not just the bartender, because the constraint is
         the venue, not the job. Service style only moves the bartender, since it
         describes what is being poured.

         Highest applicable rate wins, so the two never need a precedence
         argument and a lower override can never quietly undercut a confirmed
         rate. */
      var typeEl = root.querySelector('input[name="contact[Event type]"]:checked');
      var typeRate = typeEl ? parseFloat(typeEl.getAttribute('data-eb-type-rate')) || 0 : 0;
      var candidates = [rate, typeRate];

      if (el.getAttribute('data-eb-role') === 'bartender') {
        var styleEl = root.querySelector('[data-eb-style]:checked');
        if (styleEl) candidates.push(parseFloat(styleEl.getAttribute('data-eb-style-rate')) || 0);
      }

      var best = Math.max.apply(null, candidates);
      if (best > rate) {
        rateNote = typeRate === best ? ' — ' + typeEl.value.toLowerCase() + ' rate' : '';
        rate = best;
      }
      var amount = qty * rate * hours;
      subtotal += amount;
      lines.push({
        label: qty + ' x ' + label,
        detail: hours > 0
          ? hours + ' hrs @ ' + money(rate) + '/hr' + rateNote +
            (extraHrs > 0 ? ' (' + serviceHours + ' service + ' + extraHrs + ' setup/breakdown)' : '')
          : money(rate) + '/hr' + rateNote,
        amount: hours > 0 ? amount : null
      });
    });

    /* ---- Catalog items -------------------------------------------------
       Every selectable item declares how it bills. The stored Shopify price is
       a PACK price for glassware, cups and ice ("$18 for 36"), so pack modes
       divide the required quantity by the pack size and round UP — you cannot
       buy two-thirds of a sleeve of cups.

       Ice is seasonal: 4 lbs per guest in summer, 3 in winter, with the season
       taken from the event date rather than today's date. */
    var dateEl = root.querySelector('input[type="date"]');
    var month = null;
    if (dateEl && dateEl.value) {
      var parts = dateEl.value.split('-');
      if (parts.length === 3) month = parseInt(parts[1], 10);
    }
    // April–September inclusive counts as summer.
    var isSummer = month === null ? true : month >= 4 && month <= 9;
    var iceWinter = parseFloat(root.getAttribute('data-eb-ice-winter')) || 3;

    var barSelected = false;
    var icePounds = 0;

    [].slice.call(root.querySelectorAll('[data-eb-item]')).forEach(function (row) {
      var input = row.querySelector('input');
      if (!input || !input.checked) return;

      var mode = row.getAttribute('data-eb-mode') || 'flat';
      var price = parseFloat(row.getAttribute('data-eb-price')) || 0;
      var pack = parseFloat(row.getAttribute('data-eb-pack')) || 1;
      var perGuest = parseFloat(row.getAttribute('data-eb-per-guest')) || 0;
      var label = row.getAttribute('data-eb-label') || 'Item';
      // Alcohol-side items count adults; cups, ice and non-alcoholic count everyone.
      var basis = row.getAttribute('data-eb-basis') === 'adults' ? 'adults' : 'total';
      var heads = basis === 'adults' ? adults : totalGuests;
      var headWord = basis === 'adults' ? 'adults' : 'guests';

      if (mode === 'flat') {
        barSelected = true; // flat groups are the bars and walls
        subtotal += price;
        lines.push({ label: label, detail: '', amount: price });
        return;
      }

      if (heads <= 0) {
        lines.push({
          label: label,
          detail: basis === 'adults' ? 'needs adult count' : 'needs guest count',
          amount: null
        });
        return;
      }

      /* ---- Quantity ------------------------------------------------------
         Guest count is a STARTING POINT, not a rule. Nobody needs one of
         everything for every guest — twelve kids on lemonade while a hundred
         adults drink cocktails is the normal case. So each item carries its own
         editable quantity, seeded from the headcount and then left alone. */
      var qtyWrap = row.querySelector('[data-eb-qty-wrap]');
      var qtyInput = row.querySelector('[data-eb-qty]');
      var unitEl = row.querySelector('[data-eb-qty-unit]');

      var each = perGuest;
      var seasonNote = '';
      if (mode === 'pack_lb') {
        each = isSummer ? perGuest : iceWinter;
        seasonNote = isSummer ? 'summer' : 'winter';
      }

      /* Ice and per-guest servings are NOT divided: everyone's drink needs ice,
         and a per-guest serving is already one each. Only counted items that
         substitute for one another — glassware, cups — share the pool. */
      var shares = mode === 'pack' ? sharedCount(row) : 1;

      var central;
      if (mode === 'per_unit') {
        /* Equipment: one per bar. Not per guest, not per pack. */
        central = Math.max(1, barCount);
      } else if (mode === 'per_guest') {
        /* Full headcount is right when this is the ONLY thing guests can drink.
           Once there is more than one beverage option on the table, nobody takes
           one of each — Dennis's rule is 50-75% of the count, leaning to 75%. So
           the pre-filled figure is 75% and the suggested range opens down to 50%.
           Counted off ticked siblings in the same group, which is the same signal
           glassware already uses to share a pool. */
        var beverageChoices = sharedCount(row);
        central = beverageChoices > 1 ? heads * 0.75 : heads;
      } else {
        central = (heads * each) / shares;
      }

      /* A range, not a figure. How much a room drinks depends on the occasion,
         the length of the night and who is in it, and pretending we can predict
         it to the glass is how a client ends up over-ordering. Plus or minus a
         fifth is honest about that, and the midpoint is what gets pre-filled. */
      var lowQty, highQty;
      var suggested = Math.round(central);
      if (mode === 'per_unit') {
        // A range is meaningless on a discrete piece of kit.
        lowQty = highQty = suggested;
      } else if (mode === 'per_guest' && sharedCount(row) > 1) {
        lowQty = Math.max(1, Math.round(heads * 0.5));
        highQty = Math.max(lowQty, Math.round(heads * 0.75));
      } else {
        lowQty = Math.max(1, Math.round(central * 0.8));
        highQty = Math.max(lowQty + 1, Math.round(central * 1.2));
      }

      if (mode === 'pack' || mode === 'pack_lb') {
        suggested = roundToPack(suggested, pack);
        lowQty = floorToPack(lowQty, pack);
        highQty = ceilToPack(highQty, pack);
        // Equal is a real answer here — one pack, no meaningful range — so
        // only an actual inversion (should not happen with floor <= ceil)
        // gets patched open.
        if (highQty < lowQty) highQty = lowQty + pack;
      }

      var unitWord = mode === 'per_unit' ? (suggested === 1 ? 'unit' : 'units')
                   : (mode === 'per_guest' ? 'servings' : (mode === 'pack_lb' ? 'lbs' : 'pieces'));

      if (qtyWrap) qtyWrap.hidden = false;
      if (unitEl) {
        /* Says the number AND says who owns it. A bare figure in a box reads as
           a requirement, and people either accept it or abandon the form; a
           suggested range with "yours to change" invites the correction that
           makes the estimate accurate. */
        if (mode === 'per_unit') {
          unitEl.textContent = unitWord + ' \u00b7 one per bar' +
            (barCount > 1 ? ' (' + barCount + ' bars)' : '') +
            ' \u00b7 set it to 0 and we will confirm with you';
        } else if (lowQty === highQty) {
          unitEl.textContent = unitWord + ' \u00b7 we suggest ' +
            lowQty.toLocaleString('en-US') + ' \u00b7 yours to change';
        } else {
          unitEl.textContent = unitWord +
            ' \u00b7 we suggest ' + lowQty.toLocaleString('en-US') +
            '\u2013' + highQty.toLocaleString('en-US') +
            (shares > 1 ? ' (split across ' + shares + ' glass choices)' : '') +
            /* Was "of 100 \u2014 guests spread across the options", which pushed this
               hint past 530px of max-content width and was the thing crushing the
               row on a phone. Same meaning, roughly half the length, no dash. */
            (mode === 'per_guest' && sharedCount(row) > 1
               ? ' of ' + heads.toLocaleString('en-US') + ', split across options'
               : '') +
            ' \u00b7 yours to change';
        }
      }
      // Seed once; never overwrite a number the visitor has typed.
      // hasAttribute, NOT getAttribute: the marker is set with an empty value, and
      // getAttribute returns "" for that, which is falsy — so the guard inverted
      // and every recalc clobbered the typed quantity.
      if (qtyInput && !qtyInput.hasAttribute('data-eb-touched')) {
        qtyInput.value = Math.round(suggested);
      }
      var qty = qtyInput ? parseFloat(String(qtyInput.value).replace(/[^0-9.]/g, '')) || 0 : suggested;
      if (qty <= 0) {
        lines.push({ label: label, detail: 'set a quantity', amount: null });
        return;
      }
      var custom = Math.abs(qty - Math.round(suggested)) > 0.01;

      if (mode === 'per_unit') {
        var uAmt = price * qty;
        subtotal += uAmt;
        lines.push({
          label: label,
          detail: qty + ' ' + unitWord + ' @ ' + money(price) +
                  (barCount > 1 ? ' (' + barCount + ' bars)' : ''),
          amount: uAmt
        });
        return;
      }

      if (mode === 'per_guest') {
        var amt = price * qty;
        subtotal += amt;
        lines.push({
          label: label,
          detail: qty + ' ' + unitWord + ' @ ' + money(price) +
                  (custom ? '' : ' (' + heads + ' ' + headWord + ')'),
          amount: amt
        });
        return;
      }

      // Weight-based ice contributes to the storage-cooler threshold.
      if (row.hasAttribute('data-eb-ice-weight')) icePounds += qty;

      // pack and pack_lb — round up, since part-packs cannot be bought.
      var packs = Math.ceil(qty / pack);
      var total = packs * price;
      subtotal += total;
      lines.push({
        label: label,
        detail:
          qty.toLocaleString('en-US') + ' ' + unitWord +
          (custom ? '' : ' (' + each + ' per head' + (seasonNote ? ', ' + seasonNote : '') + ')') +
          ' — ' + packs + ' x ' + pack + ' @ ' + money(price),
        amount: total
      });
    });

    /* A storage cooler comes with larger ice orders. Counted across cubed and
       crushed together, since it is the total delivered weight that matters. */
    if (icePounds > 0) {
      var coolerAt = attr('data-eb-ice-cooler', 100);
      if (icePounds >= coolerAt) {
        lines.push({
          label: 'Ice storage cooler',
          detail: icePounds.toLocaleString('en-US') + ' lbs — included over ' + coolerAt + ' lbs',
          amount: null,
          info: 'included'
        });
      }
    }

    /* Items the visitor wants advice on, surfaced so the quote can address them. */
    var unsure = [].slice.call(root.querySelectorAll('[data-eb-unsure]'))
      .filter(function (i) { return i.checked; })
      .map(function (i) { return i.value; });
    if (unsure.length) {
      lines.push({
        label: 'Needs a recommendation',
        detail: unsure.join(', '),
        amount: null,
        info: 'we advise'
      });
    }

    /* Bar delivery is charged once, not per bar. A second-floor or stairs
       delivery REPLACES the ground-floor rate rather than adding to it — it is a
       different rate for the same job, and billing both would double-charge. */
    if (barSelected) {
      var delivery = parseFloat(root.getAttribute('data-eb-bar-delivery')) || 0;
      var upstairs = !!root.querySelector('[data-eb-upstairs]:checked');
      if (upstairs) {
        var upFee = parseFloat(root.getAttribute('data-eb-bar-delivery-upstairs')) || 0;
        if (upFee > 0) delivery = upFee;
      }
      if (delivery > 0) {
        subtotal += delivery;
        lines.push({
          label: 'Bar delivery',
          detail: upstairs ? 'one-off — second floor / stairs' : 'one-off',
          amount: delivery
        });
      }
    }

    /* Trash removal only when the venue requires it, so it never inflates a
       quote for a venue that handles its own waste. */
    if (root.querySelector('[data-eb-trash]:checked')) {
      var trash = parseFloat(root.getAttribute('data-eb-trash-fee')) || 0;
      if (trash > 0) {
        subtotal += trash;
        lines.push({ label: 'Trash removal', detail: 'one-off — venue requirement', amount: trash });
      }
    }

    /* Travel, billed per mile beyond the free radius measured from the base ZIP.
       Cities carry an approximate distance; a distance of 0 means the trip is
       long enough that lodging and logistics dominate and a per-mile figure
       would mislead, so those quote individually and add nothing here. */
    var travel = travelFee();
    if (travel.amount > 0) {
      subtotal += travel.amount;
      lines.push({ label: 'Travel', detail: travel.detail, amount: travel.amount });
    }

    /* ---- Fees ------------------------------------------------------------
       Applied in the order they actually bill: services subtotal, then the
       service charge or optional gratuity on that subtotal, then the one-off
       booking fee, then card processing on the whole charged amount — because
       that is what a processor takes a percentage of.

       Shown in the running summary rather than added at submission. A quote that
       silently grows a service charge and a card fee reads as a bait, and it is
       the fastest way to lose a booking already won.

       Evaluated twice — on the low and the high end of the services band — so the
       percentage fees scale with the band instead of the range being stretched
       across a $50 booking fee that is not uncertain at all. */
    var bookingFee = attr('data-eb-booking-fee', 50);
    var cardPct = attr('data-eb-card-fee-pct', 3.65);
    var serviceLow = attr('data-eb-service-low', 3);
    var serviceHigh = attr('data-eb-service-high', 6);
    var serviceAt = attr('data-eb-service-threshold', 100);

    var payEl = root.querySelector('[data-eb-pay]:checked');
    var payPct = payEl ? parseFloat(payEl.getAttribute('data-eb-pay-fee')) || 0 : 0;
    var payLabel = payEl ? payEl.value : '';

    var tipSel = root.querySelector('[data-eb-tip-pct]');
    var tipWrap = root.querySelector('[data-eb-tip-optional]');
    var tipCustom = root.querySelector('[data-eb-tip-custom]');
    var tipCustomWrap = root.querySelector('[data-eb-tip-custom-wrap]');

    /* "custom" is not a number, so it cannot go straight into the maths. The
       box only counts when it is the visible choice — a figure left behind in
       a hidden field must not keep charging a gratuity. */
    function tipPercent() {
      if (!tipSel) return 0;
      var custom = tipSel.value === 'custom';
      if (tipCustomWrap) tipCustomWrap.hidden = !custom;
      if (!custom) return parseFloat(tipSel.value) || 0;
      var v = parseFloat(String(tipCustom && tipCustom.value).replace(/[^0-9.]/g, ''));
      if (!isFinite(v) || v < 0) return 0;
      return Math.min(v, 100);
    }
    var autoService = totalGuests >= serviceAt;

    // Above the threshold the charge is automatic, so the optional control would
    // only invite double-charging.
    if (tipWrap) tipWrap.hidden = autoService;

    function computeFees(base, svcPerGuest) {
      var out = { lines: [], total: 0 };
      if (base <= 0) return out;

      /* Per guest, not a percentage of the services. It tracks how many people
         have to be served, which is what actually drives the crew's work — and
         it is why a low-headcount, high-rental event carries a small charge
         while a big cash bar carries a large one. */
      if (autoService) {
        var svc = totalGuests * svcPerGuest;
        out.total += svc;
        out.lines.push({
          label: 'Service charge',
          note: money(serviceLow) + '–' + money(serviceHigh) + ' per guest x ' +
            totalGuests.toLocaleString('en-US') + ' guests, goes to the crew',
          amount: svc
        });
      } else if (tipSel) {
        var tipPct = tipPercent();
        if (tipPct > 0) {
          var tip = base * (tipPct / 100);
          out.total += tip;
          out.lines.push({ label: 'Gratuity', note: tipPct + '% — optional', amount: tip });
        }
      }

      if (bookingFee > 0) {
        out.total += bookingFee;
        out.lines.push({ label: 'Booking fee', note: 'once, on reservation', amount: bookingFee });
      }

      if (payPct > 0) {
        var proc = (base + out.total) * (payPct / 100);
        out.total += proc;
        out.lines.push({
          label: 'Card processing',
          note: payPct + '% — free by bank transfer',
          amount: proc
        });
      } else if (payLabel) {
        out.lines.push({ label: 'Processing', note: 'bank transfer', amount: 0, free: true });
      }

      return out;
    }

    var lowFees = computeFees(subtotal, serviceLow);
    var highFees = computeFees(subtotal * BAND, serviceHigh);

    renderFees(lowFees.lines, highFees.lines);
    renderNudges({
      guests: totalGuests,
      adults: adults,
      hours: serviceHours,
      bars: barCount,
      isWedding: selectedEventType().indexOf('wedding') > -1,
      isMarine: !!root.querySelector('[data-eb-type-marine]:checked'),
      typeKey: selectedTypeKey()
    });
    render(lines, subtotal + lowFees.total, subtotal * BAND + highFees.total);
  }

  var feesEl = root.querySelector('[data-eb-fees]');

  function renderFees(feeLines, highLines) {
    if (!feesEl) return;
    var billable = feeLines.length > 0;
    feesEl.hidden = !billable;
    if (!billable) { feesEl.innerHTML = ''; return; }

    /* The two sets are built by the same code path in the same order, so pairing
       by index is safe. Any row whose ends differ is shown as a band rather than
       a single figure — quoting the low end of a $3-6 per-guest charge as if it
       were the price is exactly the kind of understatement that turns into an
       argument over the final invoice. */
    feesEl.innerHTML = feeLines.map(function (f, i) {
      var hi = highLines && highLines[i];
      var amt;
      if (f.free) {
        amt = 'Free';
      } else if (hi && Math.round(hi.amount) !== Math.round(f.amount)) {
        amt = money(f.amount) + ' – ' + money(hi.amount);
      } else {
        amt = money(f.amount);
      }
      return '<li class="eb-est__fee' + (f.free ? ' eb-est__fee--free' : '') + '">' +
        '<span class="eb-est__fee-main">' + escapeHtml(f.label) +
        (f.note ? '<span class="eb-est__fee-note">' + escapeHtml(f.note) + '</span>' : '') +
        '</span><span class="eb-est__fee-amt">' + amt + '</span></li>';
    }).join('');
  }

  function render(lines, low, high) {
    if (summaryList) {
      if (!lines.length) {
        summaryList.innerHTML =
          '<li class="eb-est__empty">Choose what you need and your estimate builds here.</li>';
      } else {
        summaryList.innerHTML = lines
          .map(function (l) {
            return (
              '<li class="eb-est__line"><span class="eb-est__line-main">' +
              escapeHtml(l.label) +
              (l.detail ? '<span class="eb-est__line-detail">' + escapeHtml(l.detail) + '</span>' : '') +
              '</span>' +
              (l.amount !== null && l.amount !== undefined
                ? '<span class="eb-est__line-amt">' + money(l.amount) + '</span>'
                : '<span class="eb-est__line-amt eb-est__line-amt--tbd">' +
                  escapeHtml(l.info || 'set hours') + '</span>') +
              '</li>'
            );
          })
          .join('');
      }
    }

    if (totalEl) {
      var wasShowing = (totalEl.textContent || '').trim();
      // A range, not a figure. Anchoring on one number invites a dispute over
      // the final quote; the band is honest about what is still unknown.
      if (low <= 0) {
        totalEl.textContent = '—';
        if (totalNote) totalNote.textContent = 'Add services to see an estimate range.';
      } else {
        totalEl.textContent = money(low) + ' – ' + money(high);
        if (totalNote) {
          totalNote.textContent =
            'Indicative only. Delivery, travel and taxes are quoted separately.';
        }
      }

      /* Only on a real change, and never on the first paint — highlighting a
         number the visitor has not touched yet reads as a glitch. The timer is
         cleared before it is reset so a fast series of edits cannot leave the
         highlight stuck on. */
      var nowShowing = (totalEl.textContent || '').trim();
      if (wasShowing && nowShowing !== wasShowing) {
        totalEl.setAttribute('data-eb-bumped', '');
        clearTimeout(totalEl.__ebBump);
        totalEl.__ebBump = setTimeout(function () {
          totalEl.removeAttribute('data-eb-bumped');
        }, 420);
      }
    }

    /* The briefing that heads the email.

       Shopify emails a contact form as a flat list of every field name and
       value, in submission order, and the theme cannot template that. So this
       builds one readable block and the markup puts it first — staff read the
       top and stop, instead of scanning ninety field names for the six that
       matter.

       Plain text with padded columns rather than anything clever: it has to
       survive Gmail, Apple Mail and a phone, none of which will honour markup
       we do not control. */
    if (specField) {
      specField.value = buildBriefing(lines, low, high);
    }
  }

  // Value of a field by its submitted name, trimmed, or '' when absent/blank.
  function fieldVal(name) {
    var el = form.querySelector('[name="contact[' + name + ']"]');
    if (el && (el.type === 'radio' || el.type === 'checkbox')) {
      var picked = form.querySelector('[name="contact[' + name + ']"]:checked');
      return picked ? String(picked.value).trim() : '';
    }
    return el ? String(el.value).trim() : '';
  }

  /* Always leaves a gap, even when the label is longer than the column. Padding
     to a fixed width alone produced "Best time to callAny time" and
     "The Total Barrel Package1" — the label ate the separator. */
  function pad(label, width) {
    var s = String(label);
    while (s.length < width) s += ' ';
    return s + '  ';
  }

  function block(title, rows, width) {
    var kept = rows.filter(function (r) {
      return r[1] !== '' && r[1] !== null && r[1] !== undefined;
    });
    if (!kept.length) return null;
    var out = [title.toUpperCase()];
    for (var i = 0; i < kept.length; i++) {
      out.push('  ' + pad(kept[i][0], width || 18) + kept[i][1]);
    }
    return out.join('\n');
  }

  /* The quote needs its own shape. A priced line's label can run to seventy
     characters ("3 x Bartender (9.5 hrs @ $40/hr (5 service + 4.5 setup...))"),
     so the detail goes on its own indented line underneath and the amount stays
     next to the thing it prices. */
  function quoteBlock(lines) {
    if (!lines || !lines.length) return null;
    var out = ['QUOTE'];
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      var amount = (l.amount !== null && l.amount !== undefined) ? money(l.amount) : '';
      out.push(amount ? '  ' + pad(l.label, 30) + amount : '  ' + l.label);
      if (l.detail) out.push('      ' + String(l.detail).replace(/\s+/g, ' ').trim());
    }
    return out.join('\n');
  }

  // Every non-empty text node under `el`, in order.
  function textPieces(el) {
    var out = [];
    (function walk(node) {
      for (var n = node.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3) {
          var s = n.nodeValue.replace(/\s+/g, ' ').trim();
          if (s) out.push(s);
        } else if (n.nodeType === 1) {
          walk(n);
        }
      }
    })(el);
    return out;
  }

  function feeBlock() {
    if (!feesEl || feesEl.hidden) return null;
    var items = [].slice.call(feesEl.querySelectorAll('.eb-est__fee'));
    if (!items.length) return null;
    var out = ['FEES'];
    for (var i = 0; i < items.length; i++) {
      /* TEXT NODES in document order.

         Two earlier attempts got this wrong. Reading the <li>'s textContent
         glued everything into one run ("Service charge$3-$6 per guest..."),
         because the spans sit flush against each other. Reading leaf ELEMENTS
         then lost the labels, because "Service charge" is a bare text node
         inside a wrapper rather than an element of its own. Text nodes are the
         only thing that captures all three pieces regardless of how the row
         happens to be nested. */
      var kids = textPieces(items[i]);
      if (kids.length >= 2) {
        var label = kids[0];
        var amount = kids[kids.length - 1];
        var note = kids.slice(1, -1).join(' ');
        out.push('  ' + pad(label, 30) + amount);
        if (note) out.push('      ' + note);
      } else {
        out.push('  ' + items[i].textContent.replace(/\s+/g, ' ').trim());
      }
    }
    return out.join('\n');
  }

  function buildBriefing(lines, low, high) {
    var blocks = [];
    blocks.push('EXECUTIVE BEVERAGE — ESTIMATE REQUEST\n=====================================');

    blocks.push(block('Client', [
      ['Name', fieldVal('Name')],
      ['Email', fieldVal('email')],
      ['Phone', fieldVal('Phone')],
      ['Company', fieldVal('Company or organization')],
      ['Best time', fieldVal('Best time to call')]
    ]));

    var guests = fieldVal('Total guests');
    var under21 = fieldVal('Guests under 21');
    blocks.push(block('Event', [
      ['Type', fieldVal('Event type')],
      ['Occasion', fieldVal('Occasion')],
      ['Date', fieldVal('Event date')],
      ['Location', [fieldVal('Location'), fieldVal('ZIP')].filter(Boolean).join(' ')],
      ['Address', fieldVal('Event address')],
      ['Venue', fieldVal('Venue')],
      ['Guests', guests ? guests + (under21 && under21 !== '0' ? ' (' + under21 + ' under 21)' : '') : ''],
      ['Bar hours', fieldVal('Hours of bar service')],
      ['Bar opens', fieldVal('Bar opens')],
      ['Bar closes', fieldVal('Bar closes')],
      ['Service style', fieldVal('Service style')]
    ]));

    blocks.push(block('Staff requested', [
      ['Bartenders', nz(fieldVal('Bartenders'))],
      ['Waitstaff', nz(fieldVal('Waitstaff'))],
      ['Bar-backs', nz(fieldVal('Bar-backs'))],
      ['Cocktail servers', nz(fieldVal('Cocktail servers'))],
      ['Wine pros', nz(fieldVal('Wine professionals'))],
      ['Bev. director', nz(fieldVal('Beverage Director'))]
    ]));

    /* Rentals from the ticked rows rather than the priced lines, so the office
       sees what the customer actually chose and in what quantity. */
    var picked = [];
    var rows = root.querySelectorAll('[data-eb-item]');
    for (var i = 0; i < rows.length; i++) {
      var box = rows[i].querySelector('input[type="checkbox"]');
      if (!box || !box.checked) continue;
      var qtyEl = rows[i].querySelector('[data-eb-qty]');
      var qty = qtyEl && !qtyEl.disabled ? String(qtyEl.value).trim() : '';
      picked.push([rows[i].getAttribute('data-eb-label') || box.value, qty || '1']);
    }
    blocks.push(block('Rentals & add-ons', picked, 30));

    blocks.push(quoteBlock(lines));
    blocks.push(feeBlock());
    blocks.push('INDICATIVE TOTAL  ' + (low > 0 ? money(low) + ' – ' + money(high) : 'n/a'));

    var notes = fieldVal('Notes');
    if (notes) blocks.push('NOTES\n  ' + notes);

    // One blank line between blocks; empty blocks drop out entirely.
    return blocks.filter(function (b) { return b; }).join('\n\n');
  }

  // A staffing count of zero is noise in a briefing; leave it out.
  function nz(v) {
    return v && v !== '0' ? v : '';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- The billable window ----------------------------------------------
     Every event is quoted from bar-open to bar-close, asked as two clock times
     rather than a typed duration. Setup and breakdown are deliberately NOT part
     of this figure; they are added separately to staffed hours.

     Weddings are their own case and get their own pair of fields inside the
     timeline block, because when the bar opens is a consequence of the running
     order:
       - ceremony at the same venue -> the bar opens at the ceremony start.
         Guests are already on site and standing next to it.
       - ceremony somewhere else    -> the bar opens at cocktail hour, when the
         guests actually arrive at this venue.
     Either way it is a PREFILL, not a lock — plenty of couples run it their own
     way and the form should not argue with them.

     One function owns the hours field so the three sources cannot fight over
     it. Precedence: a vessel underway (dock-to-dock is captive time) beats a
     stated window, which beats whatever was typed by hand. */
  var weddingWrap = root.querySelector('[data-eb-wedding]');
  var hoursInput = root.querySelector('[data-eb-hours] input');
  var derivedNote = root.querySelector('[data-eb-derived-hours]');
  var windowNote = root.querySelector('[data-eb-window-derived]');
  var windowFields = [].slice.call(root.querySelectorAll('[data-eb-window]'));
  var openInput = root.querySelector('[data-eb-t-open]');

  /* Pick a bar-open time and the close fills itself in at the minimum billable
     window, so the hours line and the running total populate immediately instead
     of waiting for a second entry. Same rule the wedding block already uses: it is
     ours until somebody types over it, and after that we never touch it.

     This is the difference between a form that asks two questions and a form that
     answers the second one for you. */
  var barCloseIsOurs = true;
  if (barCloseInput) {
    barCloseInput.addEventListener('input', function () { barCloseIsOurs = false; });
    barCloseInput.addEventListener('change', function () { barCloseIsOurs = false; });
  }

  function suggestBarClose() {
    if (!openInput || !barCloseInput) return;
    if (barCloseInput.value && !barCloseIsOurs) return;   // theirs — leave it
    var open = minutesOf(openInput);
    if (open === null) return;
    var hrs = parseFloat(root.getAttribute('data-eb-min-hours'));
    if (!isFinite(hrs) || hrs <= 0) hrs = 4;
    barCloseInput.value = hhmm((open + Math.round(hrs * 60)) % (24 * 60));
    barCloseIsOurs = true;
  }

  if (openInput) openInput.addEventListener('change', function () { suggestBarClose(); deriveHours(); });

  var barCloseInput = root.querySelector('[data-eb-t-barclose]');
  var wOpenInput = root.querySelector('[data-eb-t-wopen]');
  var wCloseInput = root.querySelector('[data-eb-t-wclose]');
  var wOpenHint = root.querySelector('[data-eb-wopen-hint]');

  function minutesOf(el) {
    if (!el || !el.value) return null;
    var bits = el.value.split(':');
    if (bits.length < 2) return null;
    return parseInt(bits[0], 10) * 60 + parseInt(bits[1], 10);
  }

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function hhmm(mins) {
    var m = ((mins % 1440) + 1440) % 1440;
    return pad2(Math.floor(m / 60)) + ':' + pad2(m % 60);
  }

  /* Reads back as "5" or "5.25 hours" without trailing-zero noise. */
  function hoursLabel(h) {
    return (Math.round(h * 100) / 100) + (h === 1 ? ' hour' : ' hours');
  }

  function clockLabel(el) {
    var mins = minutesOf(el);
    if (mins === null) return '';
    var h = Math.floor(mins / 60), m = mins % 60;
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + (m ? ':' + pad2(m) : '') + ampm;
  }

  function selectedEventType() {
    var checked = root.querySelector('input[name="contact[Event type]"]:checked');
    return checked ? checked.value.toLowerCase() : '';
  }

  function selectedTypeKey() {
    var checked = root.querySelector('input[name="contact[Event type]"]:checked');
    return checked ? (checked.getAttribute('data-eb-type-key') || '') : '';
  }

  /* ---- Per-type question groups -----------------------------------------
     One generic mechanism for every [data-eb-when] group, matched against the
     selected event type's key. A group can list several keys, space separated.

     Hidden inputs are DISABLED, not merely hidden. Two reasons, both of which
     have bitten this form before:
       - a hidden input with `required` blocks submission with a validation
         message the guest cannot see or reach;
       - two groups share field names on purpose (corporate and fundraiser both
         ask "Bar model"), so leaving both enabled would submit whichever the
         browser serialised last rather than the one on screen.
     Values are preserved through disable/enable, so flipping back and forth does
     not lose what someone already typed.
  ---------------------------------------------------------------------- */
  var specGroups = [].slice.call(root.querySelectorAll('[data-eb-when]'));

  function syncSpecGroups() {
    if (!specGroups.length) return;
    var key = selectedTypeKey();
    specGroups.forEach(function (group) {
      var keys = (group.getAttribute('data-eb-when') || '').split(/\s+/);
      var show = key !== '' && keys.indexOf(key) > -1;
      group.hidden = !show;
      [].slice.call(group.querySelectorAll('input, select, textarea')).forEach(function (el) {
        el.disabled = !show;
      });
    });
  }

  if (specGroups.length) {
    root.addEventListener('change', syncSpecGroups);
    syncSpecGroups();
  }

  function isWeddingSelected() {
    return selectedEventType().indexOf('wedding') > -1;
  }

  function setGroupEnabled(scope, on) {
    if (!scope) return;
    [].slice.call(scope.querySelectorAll('input, select, textarea')).forEach(function (el) {
      el.disabled = !on;
    });
  }

  /* Whether the wedding bar-open time is still ours to prefill or belongs to
     whoever is filling the form in. Programmatic value changes do not fire input
     events, so a hand-typed time is the only thing that flips this. */
  var wOpenIsOurs = true;
  if (wOpenInput) {
    wOpenInput.addEventListener('input', function () { wOpenIsOurs = false; });
    wOpenInput.addEventListener('change', function () { wOpenIsOurs = false; });
  }

  function prefillWeddingOpen() {
    if (!wOpenInput) return;
    var offsite = !!(root.querySelector('[data-eb-ceremony-offsite]') || {}).checked;
    var cocktail = minutesOf(root.querySelector('[data-eb-t-cocktail]'));

    /* The bar opens at COCKTAIL HOUR either way — never at the ceremony.
       This used to prefill the ceremony time whenever the ceremony was at the
       venue, on the reasoning that guests were already on site. That is the wrong
       call and it was going out on quotes: a bar opening during the ceremony means
       ice, glass and a queue behind the vows, and guests watching us finish setting
       up. Being ready before the ceremony and opening after it are two different
       times, and only the second one belongs in this field.

       If cocktail hour has not been entered yet there is nothing safe to prefill,
       so the field is left alone rather than filled with the ceremony time. */
    var src = cocktail;

    if (wOpenHint) {
      /* Same opening sentence as the non-wedding bar-open field, then the
         wedding-specific reason. Consistency across the form matters more than a
         bespoke sentence per branch, and it reads shorter too. */
      var STD_OPEN = 'First drink poured, not when we arrive to set up.';
      wOpenHint.textContent = offsite
        ? STD_OPEN + ' Your ceremony is elsewhere, so we open when guests arrive for cocktail hour.'
        : STD_OPEN + ' We are ready before your ceremony begins and open at cocktail hour, so nothing is half-built behind you during the vows.';
    }

    if (src === null) return;
    if (wOpenInput.value && !wOpenIsOurs) return; // chosen on purpose — leave it
    wOpenInput.value = hhmm(src);
    wOpenIsOurs = true;
  }

  function syncWedding() {
    if (!weddingWrap) return;
    var isWedding = isWeddingSelected();
    weddingWrap.hidden = !isWedding;

    /* Exactly one open/close pair may be live at a time. The two pairs share
       field names on purpose so the inbox reads the same for every event, which
       means the hidden one has to be DISABLED, not just hidden — see the note on
       hidden groups above. */
    setGroupEnabled(weddingWrap, isWedding);
    windowFields.forEach(function (el) {
      el.hidden = isWedding;
      setGroupEnabled(el, !isWedding);
    });

    if (isWedding) prefillWeddingOpen();
  }

  /* A vessel that has pushed off outranks any stated window: the crew cannot
     leave, so dock-to-dock is the billable time whatever the bar hours say. */
  function marineOwnsHours() {
    if (!root.querySelector('[data-eb-type-marine]:checked')) return false;
    if (!(root.querySelector('[data-eb-underway]') || {}).checked) return false;
    return minutesOf(root.querySelector('[data-eb-t-pushoff]')) !== null &&
      minutesOf(root.querySelector('[data-eb-t-return]')) !== null;
  }

  function deriveHours() {
    var isWedding = isWeddingSelected();
    var note = isWedding ? derivedNote : windowNote;
    var stale = isWedding ? windowNote : derivedNote;
    if (stale) stale.textContent = '';

    if (marineOwnsHours()) {
      if (note) note.textContent = '';
      return; // syncMarine owns the number in this case
    }

    var openEl = isWedding ? wOpenInput : openInput;
    var closeEl = isWedding ? wCloseInput : barCloseInput;
    var open = minutesOf(openEl);
    var close = minutesOf(closeEl);

    if (open === null || close === null) {
      if (hoursInput) hoursInput.removeAttribute('readonly');
      if (note) {
        note.textContent = (open === null && close === null)
          ? ''
          : 'Add both times and we will work out the hours for you.';
      }
      return;
    }

    /* Billed bar-open to bar-close, straight through. A bar that shuts for the
       move from cocktail hour to the reception is still staffed and still on
       site, so the window is not reduced for it — the crew is ours for the whole
       span either way. Setup and breakdown are then added on top. */
    var span = close - open;
    if (span <= 0) span += 24 * 60; // closing after midnight
    var hrs = Math.max(0, Math.round((span / 60) * 4) / 4); // quarter-hour precision

    if (hoursInput) {
      hoursInput.value = hrs;
      hoursInput.setAttribute('readonly', 'readonly');
    }
    if (note) {
      /* Two spaces, two bars. The pause is the goods crossing between them —
         spirits, ice, tools, garnish — not a bar being wheeled anywhere, which
         is what this note used to imply. */
      var twoSpaces = false;
      if (isWedding) {
        var reception = minutesOf(root.querySelector('[data-eb-t-reception]'));
        var separate = !!(root.querySelector('[data-eb-two-spaces]') || {}).checked;
        twoSpaces = separate && reception !== null && reception > open;
      }
      note.textContent = hoursLabel(hrs) + ' of bar service — ' +
        clockLabel(openEl) + ' to ' + clockLabel(closeEl) + '.' +
        (twoSpaces
          ? ' Billed straight through, including the roughly 15 minutes it takes to move the goods to the reception bar — the team is on site the whole time.'
          : '') +
        ' Setup and breakdown are added on top.';
    }
    recalc();
  }

  /* Visibility and prefill first, then the hours figure, so the derivation
     always reads whichever pair of fields is actually live. */
  function syncTimes() {
    syncWedding();
    deriveHours();
  }

  root.addEventListener('change', syncTimes);
  root.addEventListener('input', syncTimes);
  syncTimes();

  /* ---- Yacht & boat service ---------------------------------------------
     The one question that changes the whole quote is whether the vessel leaves
     the dock. Underway, the crew is captive: no relief, no restock run, no
     leaving early — so the billable window is dock to dock, not "hours of bar
     service". Derived rather than typed for the same reason as the wedding
     timeline: the number people type is the number they wish were true. */
  /* ---- Supplies: bring some of it yourself -------------------------------
     Shown only when "I'll bring some of it myself" is chosen, and disabled while
     hidden. The disable matters: Shopify posts every enabled field in the form,
     so a hidden-but-enabled checklist would put "Client supplies: Ice" in the
     inbox for someone who chose to have us bring everything. */
  var suppliesWrap = root.querySelector('[data-eb-supplies-block]');

  function syncSupplies() {
    if (!suppliesWrap) return;
    var some = !!root.querySelector('[data-eb-supplies-some]:checked');
    suppliesWrap.hidden = !some;
    setGroupEnabled(suppliesWrap, some);
    if (!some) {
      [].slice.call(suppliesWrap.querySelectorAll('input[type="checkbox"]')).forEach(function (el) {
        el.checked = false;
      });
    }
  }

  if (suppliesWrap) {
    root.addEventListener('change', syncSupplies);
    syncSupplies();
  }

  var marineWrap = root.querySelector('[data-eb-marine-block]');
  var marineTimes = root.querySelector('[data-eb-underway-only]');
  var marineNote = root.querySelector('[data-eb-marine-derived]');

  function syncMarine() {
    if (!marineWrap) return;
    var isMarine = !!root.querySelector('[data-eb-type-marine]:checked');
    marineWrap.hidden = !isMarine;

    if (!isMarine) {
      if (marineNote) marineNote.textContent = '';
      /* Hand the hours field back rather than simply unlocking it — the stated
         bar window may well own it now, and deriveHours knows which. */
      deriveHours();
      return;
    }

    var underway = !!(root.querySelector('[data-eb-underway]') || {}).checked;
    if (marineTimes) marineTimes.hidden = !underway;

    if (!underway) {
      deriveHours();
      if (marineNote) {
        marineNote.textContent = root.querySelector('[data-eb-dock]:checked')
          ? 'Docked service — we can restock from the dock, so we quote this like a shoreside event.'
          : 'Tell us whether the boat stays docked or pushes off. It changes the crew plan and the hours.';
      }
      return;
    }

    var off = minutesOf(root.querySelector('[data-eb-t-pushoff]'));
    var back = minutesOf(root.querySelector('[data-eb-t-return]'));
    if (off === null || back === null) {
      if (marineNote) {
        marineNote.textContent =
          'Add the push-off and return times — once we leave the dock the crew is aboard until you are back, so we bill dock to dock.';
      }
      deriveHours();
      return;
    }

    var span = back - off;
    if (span <= 0) span += 24 * 60; // returning after midnight
    var hrs = Math.max(0, Math.round((span / 60) * 4) / 4);

    if (hoursInput) {
      hoursInput.value = hrs;
      hoursInput.setAttribute('readonly', 'readonly');
    }
    if (marineNote) {
      marineNote.textContent =
        hrs + ' hours dock to dock. The crew boards before you push off and cannot ' +
        'be relieved until you are back alongside, so that is the billable window. ' +
        'Load-in is quoted separately.';
    }
    recalc();
  }

  if (marineWrap) {
    root.addEventListener('change', syncMarine);
    root.addEventListener('input', syncMarine);
    syncMarine();
  }

  /* ---- Contextual add-ons ----------------------------------------------
     Suggestions that appear in place, only once the answers make them true, and
     that fill the field they are talking about. Deliberately not modal: an
     interstitial over a half-finished form loses people. Each is dismissible and
     stays dismissed, because a suggestion that keeps coming back is an advert.

     Every rule below is a real operational one — guest-to-bartender ratios, ice,
     a bar-back on a busy bar — not an upsell dressed as advice. That is the only
     reason it is worth putting in front of someone mid-quote. */
  var dismissed = {};

  var NUDGES = [
    /* Suggestive, not automatic. Dennis's rule is that a staffed job with
       rentals needs the tools package and an ice well per two bartenders —
       true, and the reason kits arrive incomplete. But silently adding charges
       to someone's quote is how a quote stops being trusted, so these surface
       as an explained suggestion with a one-tap add. */
    {
      id: 'bartools',
      slot: 'items',
      icon: '🍸',
      when: function (c) {
        return numFieldValue('#eb-bartenders') > 0 && !rowTicked('bar tools') && !!rowByLabel('bar tools');
      },
      title: function () { return 'Your bartenders will need tools'; },
      text: function () {
        return 'Shakers, strainers, jiggers, pourers, a knife and board. A bar with staff ' +
          'and no tools is the single most common thing people forget, and it is $60 for ' +
          'the whole event rather than per bartender.';
      },
      add: function () { tickRow('bar tools'); }
    },
    {
      id: 'icewell',
      slot: 'items',
      icon: '🧊',
      when: function (c) {
        return numFieldValue('#eb-bartenders') >= 2 && !rowTicked('ice well') && !!rowByLabel('ice well');
      },
      title: function () {
        var n = iceWellsFor(numFieldValue('#eb-bartenders'));
        return n === 1 ? 'Add a rolling ice well' : 'Add ' + n + ' rolling ice wells';
      },
      text: function () {
        var b = numFieldValue('#eb-bartenders');
        var n = iceWellsFor(b);
        return 'One per two bartenders — ' + b + ' bartenders works out to ' + n + '. It keeps ' +
          'drinking ice at the bartender\u2019s hip instead of in a cooler on the floor, which is ' +
          'the difference between a fast bar and a slow one.';
      },
      add: function () { tickRow('ice well', iceWellsFor(numFieldValue('#eb-bartenders'))); }
    },
    {
      /* Cross-sell, and the one place on the form where it is genuinely useful
         rather than a nag: someone who has ticked a self-serve station has told us
         they want a station, and both halves of the answer are already rows they
         can tick. Suppressed the moment either is on, so it never argues with a
         choice already made. */
      id: 'lemonade-station',
      slot: 'items',
      icon: '🍋',
      when: function () {
        var wants = rowTicked('tea & lemonade') || rowTicked('shaken lemonade') ||
                    rowTicked('iced tea') || rowTicked('infused water');
        var has = rowTicked('quarter bar') || rowTicked('custom vinyl');
        return wants && !has && !!rowByLabel('quarter bar');
      },
      title: function () { return 'Give the station a bar of its own'; },
      text: function () {
        return 'A self-serve station on a banquet table reads as an afterthought, and it is the ' +
          'first thing guests walk to. The Quarter Bar is the small footprint built for exactly ' +
          'this, and custom vinyl on the front puts the occasion \u2014 or your name \u2014 where ' +
          'everyone queueing for a drink is already looking.';
      },
      add: function () { tickRow('quarter bar', 1); tickRow('custom vinyl', 1); }
    },
    {
      /* Advisory only — no "Add it". Bars are not one of the tickable rows on this
         form, so there is nothing to add; pretending otherwise would give them a
         button that does nothing. The point is that they hear it from us before the
         quote comes back with two bars on it and reads like a surprise. */
      id: 'secondbar',
      slot: 'items',
      icon: '🍹',
      when: function () {
        return isWeddingSelected() &&
          !!(root.querySelector('[data-eb-two-spaces]') || {}).checked;
      },
      title: function () { return 'Two spaces means two bars'; },
      text: function () {
        return 'We do not wheel a bar between rooms mid-event — the cocktail hour bar stays ' +
          'where it is set and a second bar goes in the reception space. What moves is the ' +
          'goods, and that crossover is about 15 minutes of no service. Your quote will show ' +
          'a bar for each space.';
      }
    },
    {
      id: 'bartenders',
      slot: 'staffing',
      icon: '👤',
      when: function (c) { return c.guests >= 25 && numFieldValue('#eb-bartenders') === 0; },
      title: function (c) { return 'You will want ' + bartendersFor(c.guests) + ' bartenders'; },
      text: function (c) {
        return 'We plan one bartender per 50 guests so the line never builds. ' +
          c.guests.toLocaleString('en-US') + ' guests works out to ' + bartendersFor(c.guests) + '.';
      },
      add: function (c) { setField('#eb-bartenders', bartendersFor(c.guests)); }
    },
    {
      id: 'barback',
      slot: 'staffing',
      icon: '🧊',
      when: function (c) { return c.guests >= 150 && numFieldValue('#eb-barbacks') === 0; },
      title: function (c) {
        var n = barbacksFor(c.guests);
        return n === 1 ? 'Add a bar-back at this size' : 'Add ' + n + ' bar-backs at this size';
      },
      text: function (c) {
        var n = barbacksFor(c.guests);
        return 'One per 150 guests — ' + c.guests.toLocaleString('en-US') + ' works out to ' + n +
          '. Past this size the bartender spends the night restocking instead of pouring, and a ' +
          'bar-back keeping ice, garnish and glassware moving is the cheapest way to make the ' +
          'bar faster. A heavier cocktail menu is the other reason to add one.';
      },
      add: function (c) { setField('#eb-barbacks', barbacksFor(c.guests)); }
    },
    {
      id: 'servers',
      slot: 'staffing',
      icon: '🥂',
      when: function (c) {
        return c.guests >= 75 && numFieldValue('#eb-cocktail-servers') === 0 &&
          numFieldValue('#eb-bartenders') > 0;
      },
      title: function () { return 'Passed drinks instead of a queue?'; },
      text: function () {
        return 'Cocktail servers work the room with trays — welcome drinks on arrival, ' +
          'champagne for the toast, and topped-up glasses at the tables. It is the ' +
          'difference guests actually remember, and it takes pressure off the bar.';
      },
      add: function (c) { setField('#eb-cocktail-servers', Math.max(1, Math.round(c.guests / 100))); }
    },
    {
      id: 'style',
      slot: 'staffing',
      icon: '🍷',
      when: function (c) { return c.guests > 0 && !root.querySelector('[data-eb-style]:checked'); },
      title: function () { return 'Pick a service style'; },
      text: function () {
        return 'Beer and wine only is a perfectly good answer and it keeps the shopping ' +
          'list short. Choosing now means the estimate and your list match what you ' +
          'actually want poured.';
      },
      add: null
    },
    {
      id: 'marine-provision',
      slot: 'staffing',
      icon: '⚓',
      when: function (c) {
        return c.isMarine && !root.querySelector('input[name="contact[Provisioning]"]:checked');
      },
      title: function () { return 'Want us to provision the boat?' },
      text: function () {
        return 'There is no running out to a store once you are on the water. We can buy, ' +
          'load and stock everything before you board — answer the provisioning question ' +
          'above and we will price it.';
      },
      add: null
    },
    {
      id: 'wedding-flutes',
      slot: 'items',
      icon: '🥂',
      when: function (c) { return c.isWedding && c.guests > 0 && !hasAnyChecked('flute'); },
      title: function () { return 'Champagne flutes for the toast'; },
      text: function () {
        return 'Almost every wedding toasts, and almost every couple forgets the glassware ' +
          'until the week before. Flutes come by the rack — find them in the glassware list above.';
      },
      add: null
    },

    /* ---- Per-type upsell and cross-sell ---------------------------------
       Each one earns its place by being the thing that event type actually
       forgets, rather than a generic "add more". They fire off the type key, so
       a guest only ever sees suggestions that apply to what they are planning.
    -------------------------------------------------------------------- */
    {
      id: 'wedding-signature',
      slot: 'items',
      icon: '💍',
      when: function (c) { return c.typeKey === 'wedding' && c.guests > 0; },
      title: function () { return 'Two signature cocktails, one for each of you'; },
      text: function () {
        return 'His-and-hers, hers-and-hers, his-and-his — we build them with you, name them ' +
          'after you, and print the menu cards. It costs nothing extra on the bar and it is ' +
          'the detail guests photograph.';
      },
      add: null
    },
    {
      id: 'corp-branded',
      slot: 'items',
      icon: '🏢',
      when: function (c) {
        return c.typeKey === 'corporate' &&
          !root.querySelector('input[name="contact[Branded cocktail]"]:checked');
      },
      title: function () { return 'Put the company name on a cocktail'; },
      text: function () {
        return 'A named signature drink with printed menu cards turns the bar into part of the ' +
          'brand rather than a line item. Tick the branded cocktail box above and we will build it.';
      },
      add: function () { checkBox('input[name="contact[Branded cocktail]"]'); }
    },
    {
      id: 'corp-na',
      slot: 'items',
      icon: '🍋',
      when: function (c) { return c.typeKey === 'corporate' && c.guests >= 40; },
      title: function () { return 'A non-alcoholic station alongside the bar'; },
      text: function () {
        return 'At a work event a good chunk of the room is not drinking, and nobody wants to be ' +
          'the person holding a soda. A shaken lemonade or infused water station gives them ' +
          'something as good as what everyone else has.';
      },
      add: null
    },
    {
      id: 'fund-payments',
      slot: 'staffing',
      icon: '💳',
      when: function (c) {
        var model = root.querySelector('input[name="contact[Bar model]"]:checked');
        var paid = model && /cash|ticket|donation/i.test(model.value);
        return c.typeKey === 'fundraiser' && paid &&
          !root.querySelector('input[name="contact[We handle payments at the bar]"]:checked');
      },
      title: function () { return 'Let us run the money too'; },
      text: function () {
        return 'If guests are paying, someone has to take cards, count cash and reconcile it at ' +
          'the end of the night. Our team can do all of it and hand you a clean report — it is ' +
          'one less volunteer you have to find.';
      },
      add: function () { checkBox('input[name="contact[We handle payments at the bar]"]'); }
    },
    {
      id: 'fest-points',
      slot: 'staffing',
      icon: '⛺',
      when: function (c) {
        var att = numFieldValue('#eb-fest-attendance');
        var pts = numFieldValue('#eb-fest-points');
        return c.typeKey === 'festival' && att >= 1000 && pts > 0 && att / pts > 1200;
      },
      title: function () { return 'That is a lot of people per bar'; },
      text: function () {
        var att = numFieldValue('#eb-fest-attendance');
        var pts = numFieldValue('#eb-fest-points');
        return Math.round(att / pts).toLocaleString('en-US') + ' attendees per service point will ' +
          'queue. Festivals generally run better with more, smaller bars than one big one — tell us ' +
          'the footprint and we will lay it out.';
      },
      add: null
    },
    {
      id: 'program-director',
      slot: 'staffing',
      icon: '📋',
      when: function (c) {
        return c.typeKey === 'program' &&
          !root.querySelector('input[name="contact[Beverage director]"]:checked');
      },
      title: function () { return 'Add a day-of Beverage Director'; },
      text: function () {
        return 'On a multi-stakeholder event somebody has to own the whole beverage operation — ' +
          'staff, stock, compliance, the numbers at the end. That is what a Beverage Director does, ' +
          'and it is what makes a program auditable.';
      },
      add: function () { checkBox('input[name="contact[Beverage director]"]'); }
    },
    {
      id: 'private-ice',
      slot: 'items',
      icon: '🧊',
      when: function (c) {
        var sel = root.querySelector('#eb-priv-ice');
        var needsIce = sel && /no ice|Nothing/i.test(sel.value);
        return c.typeKey === 'private' && needsIce && !hasAnyChecked('ice');
      },
      title: function () { return 'You will need ice'; },
      text: function () {
        return 'You told us there is no ice on site. A bar goes through far more than people ' +
          'expect — roughly a pound and a half per guest. Add it under Bar add-ons and stop ' +
          'worrying about it.';
      },
      add: null
    }
  ];

  function checkBox(sel) {
    var el = root.querySelector(sel);
    if (!el || el.disabled) return;
    el.checked = true;
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Find a catalogue row by its label, so a nudge can tick a real product
     rather than inventing a line the quote cannot explain. */
  function rowByLabel(needle) {
    var rows = root.querySelectorAll('[data-eb-item]');
    for (var i = 0; i < rows.length; i++) {
      var l = (rows[i].getAttribute('data-eb-label') || '').toLowerCase();
      if (l.indexOf(needle.toLowerCase()) > -1) return rows[i];
    }
    return null;
  }

  function rowTicked(needle) {
    var r = rowByLabel(needle);
    if (!r) return false;
    var b = r.querySelector('input[type="checkbox"]');
    return !!(b && b.checked);
  }

  function tickRow(needle, qty) {
    var r = rowByLabel(needle);
    if (!r) return;
    var b = r.querySelector('input[type="checkbox"]');
    if (b && !b.checked) {
      b.checked = true;
      b.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (qty) {
      var q = r.querySelector('[data-eb-qty]');
      if (q) {
        q.value = String(qty);
        q.setAttribute('data-eb-touched', '');
        q.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    r.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function iceWellsFor(bartenders) {
    return Math.max(1, Math.ceil(bartenders / 2));
  }

  /* Dennis's actual ratios, replacing a 1-per-75 guess that was under-staffing
     every quote it touched:
       bartenders  1 per 50 guests
       bar-backs   1 per 150 guests, for volume or a heavier menu
       waitstaff   1 per 50 guests
     Ceil, not round, and never below one — half a bartender is not a thing, and
     rounding down is how a bar gets a queue. */
  function bartendersFor(guests) {
    return Math.max(1, Math.ceil(guests / 50));
  }

  function barbacksFor(guests) {
    return Math.max(1, Math.ceil(guests / 150));
  }

  function waitstaffFor(guests) {
    return Math.max(1, Math.ceil(guests / 50));
  }

  function numFieldValue(sel) {
    var el = root.querySelector(sel);
    return el ? parseFloat(String(el.value).replace(/[^0-9.]/g, '')) || 0 : 0;
  }

  function setField(sel, value) {
    var el = root.querySelector(sel);
    if (!el) return;
    el.value = value;
    // Mark it as deliberate so the guest-count sync cannot overwrite it later.
    el.setAttribute('data-eb-touched', '');
    recalc();
  }

  function hasAnyChecked(needle) {
    return [].slice.call(root.querySelectorAll('[data-eb-item] input[type=checkbox]'))
      .some(function (i) {
        var row = i.closest('[data-eb-item]');
        var label = row ? (row.textContent || '').toLowerCase() : '';
        return i.checked && label.indexOf(needle) > -1;
      });
  }

  var nudgeSlots = {};
  [].slice.call(root.querySelectorAll('[data-eb-nudges]')).forEach(function (el) {
    nudgeSlots[el.getAttribute('data-eb-nudges')] = el;
  });

  function renderNudges(ctx) {
    // recalc() can fire from the wedding/marine sync before this section has been
    // reached on first parse. Nothing to draw yet, and nothing to fail on.
    if (!nudgeSlots) return;
    Object.keys(nudgeSlots).forEach(function (name) {
      var slot = nudgeSlots[name];
      var active = NUDGES.filter(function (n) {
        return n.slot === name && !dismissed[n.id] && n.when(ctx);
      });

      slot.hidden = active.length === 0;
      // Only rewrite when the set actually changed, so an open nudge does not
      // re-animate on every keystroke.
      var key = active.map(function (n) { return n.id + ':' + n.title(ctx); }).join('|');
      if (slot.getAttribute('data-eb-nudge-key') === key) return;
      slot.setAttribute('data-eb-nudge-key', key);

      slot.innerHTML = active.map(function (n) {
        return '<div class="eb-est__nudge" data-eb-nudge-id="' + n.id + '">' +
          '<span class="eb-est__nudge-mark" aria-hidden="true">' + n.icon + '</span>' +
          '<span class="eb-est__nudge-body">' +
            '<span class="eb-est__nudge-title">' + escapeHtml(n.title(ctx)) + '</span>' +
            '<span class="eb-est__nudge-text">' + escapeHtml(n.text(ctx)) + '</span>' +
          '</span>' +
          '<span class="eb-est__nudge-actions">' +
            (n.add ? '<button type="button" class="eb-est__nudge-add" data-eb-nudge-add>Add it</button>' : '') +
            '<button type="button" class="eb-est__nudge-no" data-eb-nudge-no>' +
              (n.add ? 'No thanks' : 'Dismiss') + '</button>' +
          '</span>' +
        '</div>';
      }).join('');
    });
  }

  root.addEventListener('click', function (e) {
    var card = e.target.closest('[data-eb-nudge-id]');
    if (!card) return;
    var n = NUDGES.filter(function (x) { return x.id === card.getAttribute('data-eb-nudge-id'); })[0];
    if (!n) return;

    if (e.target.closest('[data-eb-nudge-add]') && n.add) {
      dismissed[n.id] = true;
      n.add({
        guests: numOf(root.querySelector('[data-eb-guests]')),
        isWedding: selectedEventType().indexOf('wedding') > -1,
        isMarine: !!root.querySelector('[data-eb-type-marine]:checked')
      });
    } else if (e.target.closest('[data-eb-nudge-no]')) {
      dismissed[n.id] = true;
      recalc();
    }
  });

  /* ---- Travel ----------------------------------------------------------
     Per-mile beyond a free radius, measured from the base ZIP. Declared as a
     function so recalc() can call it before this point in the file; function
     declarations hoist, the vars it reads are attributes on the section.

     The old copy told all seven Hampton Roads cities they were in a "core
     service area with no travel surcharge". A ten-mile radius from 23451 does
     not reach Norfolk or Chesapeake, so that promise contradicted the rate as
     soon as the rate existed. Zone now drives tone only; the number drives the
     message. */
  function travelFee() {
    var cityEl = root.querySelector('[data-eb-city]');
    if (!cityEl) return { amount: 0, detail: '', miles: null };
    var opt = cityEl.options[cityEl.selectedIndex];
    if (!opt) return { amount: 0, detail: '', miles: null };

    /* Distance comes from the shared component when it is available, so a ZIP
       sharpens the figure and all three forms agree. The city-only maths below
       stays as the fallback: if eb-travel.js fails to load, travel still prices
       rather than silently becoming free. */
    if (window.EBTravel) {
      var d = window.EBTravel.resolve(root);
      var c = window.EBTravel.cost(root, d.miles);
      window.EBTravel.paint(root);
      if (d.quoteOnly) return { amount: 0, detail: '', miles: 0 };
      if (isFinite(d.miles)) {
        var over = Math.max(0, d.miles - c.freeMiles);
        return {
          amount: Math.round(c.amount * 100) / 100,
          miles: d.miles,
          detail: over === 0
            ? 'inside the ' + c.freeMiles + '-mile radius'
            : over + ' miles beyond the first ' + c.freeMiles + ' at $' + c.rate.toFixed(2) +
              '/mile' + (c.roundTrip ? ', each way' : '') +
              (d.basis === 'city' ? ' (city estimate)' : '')
        };
      }
    }

    var miles = parseFloat(opt.getAttribute('data-eb-miles'));
    var rate = parseFloat(root.getAttribute('data-eb-travel-rate')) || 0;
    var free = parseFloat(root.getAttribute('data-eb-travel-free-miles'));
    if (isNaN(free)) free = 0;
    var roundTrip = root.getAttribute('data-eb-travel-round-trip') === 'true';

    if (isNaN(miles) || miles <= 0 || rate <= 0) return { amount: 0, detail: '', miles: miles };

    /* The stated distance is one way. Billing each way doubles it, but the
       DETAIL still quotes the one-way figure, because "95 miles beyond the
       first 10, each way" is a sentence someone can check against a map.
       "190 miles beyond the first 10" is not. */
    var oneWay = Math.max(0, miles - free);
    var billable = roundTrip ? oneWay * 2 : oneWay;
    var amount = Math.round(billable * rate * 100) / 100;

    var detail = oneWay === 0
      ? 'inside the ' + free + '-mile radius'
      : oneWay + ' miles beyond the first ' + free + ' at $' + rate.toFixed(2) + '/mile' +
        (roundTrip ? ', each way' : '');
    return { amount: amount, detail: detail, miles: miles };
  }

  /* ---- Service-area hint ----------------------------------------------- */
  var cityEl = root.querySelector('[data-eb-city]');
  var cityNote = root.querySelector('[data-eb-city-note]');
  if (cityEl && cityNote) {
    var syncCityNote = function () {
      var opt = cityEl.options[cityEl.selectedIndex];
      var zone = opt ? opt.getAttribute('data-eb-zone') : '';
      var t = travelFee();

      if (!zone) {
        cityNote.textContent = '';
        cityNote.removeAttribute('data-eb-tone');
        return;
      }
      if (t.miles === 0 || isNaN(t.miles)) {
        cityNote.textContent = 'We travel for this. Tell us the venue and we will quote the travel with the estimate.';
        cityNote.setAttribute('data-eb-tone', 'info');
      } else if (t.amount === 0) {
        cityNote.textContent = 'Inside our ' + (root.getAttribute('data-eb-travel-free-miles') || 10) +
          '-mile radius, so no travel charge.';
        cityNote.setAttribute('data-eb-tone', 'good');
      } else {
        /* Whole dollars, matching how the summary renders the same figure. Two
           different roundings of one number reads as a mistake. */
        cityNote.textContent = 'About ' + t.miles + ' miles out, so travel is roughly $' +
          Math.round(t.amount).toLocaleString('en-US') + '. We confirm it from the venue address.';
        cityNote.setAttribute('data-eb-tone', 'info');
      }
    };
    cityEl.addEventListener('change', syncCityNote);
    var zipEl = root.querySelector('[data-eb-travel-zip]');
    if (zipEl) {
      zipEl.addEventListener('input', function () { syncCityNote(); recalc(); });
      zipEl.addEventListener('change', function () { syncCityNote(); recalc(); });
    }
    syncCityNote();
  }


  /* ---- Draft rescue ----------------------------------------------------
     A long form loses people who do not yet know their headcount, or who have
     to go and ask the venue about ice. This keeps everything they typed so they
     can leave and come back.

     Stored in localStorage, which means THIS BROWSER ONLY. A resume-by-email
     link would need a server and a theme does not have one, so the notice says
     "in this browser" rather than implying anything stronger.

     Cleared on a successful submit, and after 30 days, so a stale draft from a
     previous event never resurfaces mid-enquiry.
  ---------------------------------------------------------------------- */
  var DRAFT_KEY = 'eb-estimate-draft-v1';
  var DRAFT_TTL = 30 * 24 * 60 * 60 * 1000;
  var savedNote = root.querySelector('[data-eb-saved]');
  var resumeBar = root.querySelector('[data-eb-resume]');

  function storage() {
    try {
      var k = '__eb_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (e) {
      return null;   // private mode, or storage disabled
    }
  }
  var store = storage();

  function fieldKey(el) {
    if (el.type === 'checkbox' || el.type === 'radio') {
      return (el.name || el.id) + '|' + el.value;
    }
    return el.name || el.id;
  }

  function collect() {
    var data = {};
    [].slice.call(form.querySelectorAll('input, select, textarea')).forEach(function (el) {
      if (!el.name && !el.id) return;
      if (el.type === 'hidden' || el.type === 'submit' || el.type === 'button') return;
      var k = fieldKey(el);
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) data[k] = true;
      } else if (el.value !== '' && !el.readOnly) {
        data[k] = el.value;
      }
    });
    return data;
  }

  var saveTimer = null;
  function saveDraft() {
    if (!store) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      var fields = collect();
      if (!Object.keys(fields).length) return;
      try {
        store.setItem(DRAFT_KEY, JSON.stringify({ at: Date.now(), step: current, fields: fields }));
      } catch (e) { return; }
      if (savedNote) {
        savedNote.textContent = 'Progress saved in this browser';
        savedNote.hidden = false;
      }
    }, 500);
  }

  function readDraft() {
    if (!store) return null;
    try {
      var raw = store.getItem(DRAFT_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || !d.fields || !d.at) return null;
      if (Date.now() - d.at > DRAFT_TTL) { store.removeItem(DRAFT_KEY); return null; }
      return d;
    } catch (e) { return null; }
  }

  function clearDraft() {
    if (!store) return;
    try { store.removeItem(DRAFT_KEY); } catch (e) {}
    if (savedNote) savedNote.hidden = true;
  }

  function applyDraft(d) {
    [].slice.call(form.querySelectorAll('input, select, textarea')).forEach(function (el) {
      if (!el.name && !el.id) return;
      var k = fieldKey(el);
      if (!(k in d.fields)) return;
      if (el.type === 'checkbox' || el.type === 'radio') el.checked = true;
      else el.value = d.fields[k];
    });
    /* One change event on the form so every dependent bit recalculates: the
       type-specific groups, the wedding timeline, travel, and the total. */
    form.dispatchEvent(new Event('change', { bubbles: true }));
    form.dispatchEvent(new Event('input', { bubbles: true }));
    reached = Math.max(reached, d.step || 0);
    show(d.step || 0);
  }

  function ago(ts) {
    var mins = Math.round((Date.now() - ts) / 60000);
    if (mins < 2) return 'Saved a moment ago.';
    if (mins < 60) return 'Saved ' + mins + ' minutes ago.';
    var hrs = Math.round(mins / 60);
    if (hrs < 24) return 'Saved ' + hrs + (hrs === 1 ? ' hour' : ' hours') + ' ago.';
    var days = Math.round(hrs / 24);
    return 'Saved ' + days + (days === 1 ? ' day' : ' days') + ' ago.';
  }

  (function initDraft() {
    if (!store) return;
    var d = readDraft();
    if (d && resumeBar) {
      var when = resumeBar.querySelector('[data-eb-resume-when]');
      if (when) when.textContent = ago(d.at) + ' Saved in this browser only.';
      resumeBar.hidden = false;
      var yes = resumeBar.querySelector('[data-eb-resume-yes]');
      var no = resumeBar.querySelector('[data-eb-resume-no]');
      if (yes) yes.addEventListener('click', function () { resumeBar.hidden = true; applyDraft(d); });
      if (no) no.addEventListener('click', function () { resumeBar.hidden = true; clearDraft(); });
    }
    form.addEventListener('input', saveDraft);
    form.addEventListener('change', saveDraft);
    form.addEventListener('submit', clearDraft);
  })();


  /* ---- Follow-up questions -------------------------------------------
     A field marked [data-eb-reveal-when="A|B"] appears only when the nearest
     preceding [data-eb-reveal-field] holds one of those values. Used so we only
     ask what an existing bar looks like when there is an existing bar.

     Hidden AND disabled, so a blank follow-up never lands in the inbox and a
     required attribute inside a hidden field could never block submission. */
  (function followUps() {
    var source = root.querySelector('[data-eb-reveal-field]');
    var targets = [].slice.call(root.querySelectorAll('[data-eb-reveal-when]'));
    if (!source || !targets.length) return;

    function sync() {
      var val = source.value;
      targets.forEach(function (el) {
        var allowed = (el.getAttribute('data-eb-reveal-when') || '').split('|');
        var show = allowed.indexOf(val) > -1;
        el.hidden = !show;
        [].slice.call(el.querySelectorAll('input, select, textarea')).forEach(function (f) {
          f.disabled = !show;
        });
      });
    }
    source.addEventListener('change', sync);
    sync();
  })();

  /* Both initial renders — a fresh load and a restored draft — happen while
     booted is false, so neither scrolls. Everything after this point does. */
  show(0);
  booted = true;
  recalc();
})();
